import {
  getAccountList,
  getPortalComponentAccess,
  getProjectPinnedStatus,
  getUserProjects
} from "api/collections"
import AccountProvider from "components/common/Context/AccountContext"
import { ConfirmModal } from "components/common/modal"
import { NOT_ACCESSIBLE_TO_CLIENT } from "components/common/noData/constants"
import {
  allChangeRequestStates,
  allIncidentStates,
  modularityArray
} from "constants/Constants"
import getProjectBonding from "extensions/usoc/api/itsm/fetchProjectBonding"
import { getCrCount, getIncidentCount } from "extensions/usoc/api/service"
import React, { useContext, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import { settingsActions, userActions } from "store"
import useSWR from "swr"
import { useExtension, useUserAuth } from "utils"
import { batchAPICalls, retryFailedRequests } from "utils/retryFailedRequests"

import { pageNames } from "../../constants/StringConstants"


// async function fetchIncidentData(auth, extension, projectId, params) {
//   const host = extension?.config?.env?.clientApiGateway?.URL
//   const results = []
//   for (const param of params) {
//     const result = await retryFailedRequests({ auth, host }, projectId, param, getIncidentCount)
//     results.push(result)
//   }
//   return results
// }

// async function fetchCrData(auth, extension, projectId, params) {
//   const host = extension?.config?.env?.clientApiGateway?.URL
//   const results = []
//   for (const param of params) {
//     const result = await retryFailedRequests({ auth, host }, projectId, param, getCrCount)
//     results.push(result)
//   }
//   return results
// }

function isEmptyAccRequired(incidentData, crData) {
  return Boolean(
    Object.values(incidentData || {}).some((count) => count) ||
    Object.values(crData || {}).some((count) => count)
  )
}

function isEmptyAccPresent(accounts) {
  return Boolean(accounts.find((acc) => acc.account_number === "EMPTY"))
}

function addEmptyAcc(accounts, projectName = "") {
  return [
    ...accounts,
    {
      account_environment: "Unassigned",
      account_name: "Unassigned",
      account_number: "EMPTY",
      project_name: projectName
    }
  ]
}

function removeEmptyAcc(accounts) {
  return accounts.filter((acc) => acc.account_number !== "EMPTY")
}

const getPageName = (pathname) => {
  for (const [pageName, pagePath] of Object.entries(pageNames)) {
    if (pagePath === pathname) {
      return pageName
    }
  }
  return "current"
}

// All the data related project and accounts are fetched here
export default function ProjectAccountsMonitor() {
  const dispatch = useDispatch()
  const auth = useUserAuth()
  const extension = useExtension()
  const { pathname } = useLocation()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [apiFailed, setApiFailed] = useState(false)

  const {
    accounts,
    selectedAccounts,
    setSelectedAccounts,
    setAllAccounts,
    setCloudabilityData,
    isEmptyAccSelected,
    setProjectBonding,
    setProjectBondLoading
  } = useContext(AccountProvider)

  const selectedProjectId = useSelector((state) => {
    return state.settings.currentProject
  })

  const userProjectNames = useSelector((state) => {
    return state.user.projectNames
  })

  const user = useSelector((state) => {
    return state.user.email.split("@")[0]
  })

  const currentProjectName =
    userProjectNames?.filter(
      (project) => project.u_number === selectedProjectId
    )[0]?.u_project_name ?? "unknown"

  const currentPageName = getPageName(pathname)

  const clientView = useSelector((state) => state.user.clientView)

  const createProjectObject = (uNumber, projectName, isPinned = false) => {
    return {
      u_number: uNumber,
      u_project_name: projectName?.join(" - ").trim(),
      u_project_pinned: isPinned
    }
  }

  const host = extension?.config?.env?.clientApiGateway?.URL

  useEffect(() => {
    ; (async () => {
      dispatch(userActions.userProjectsFetching(true))
      const projectsPinnedStatus =
        (await getProjectPinnedStatus({ auth, host }, user)).data
          ?.split(",")
          .reverse() || []

      const projects = (await getUserProjects({ auth, host }, user)).data
      const pinnedProjects = []
      const projectData = projects?.flatMap((project) => {
        const projectName = project.split(" - ")
        const uNumber = projectName?.shift()?.trim()
        if (projectsPinnedStatus?.includes(uNumber)) {
          const index = projectsPinnedStatus.indexOf(uNumber)
          pinnedProjects[index] = createProjectObject(
            uNumber,
            projectName,
            true
          )
          return []
        }
        return [createProjectObject(uNumber, projectName)]
      })

      const projectNames = [
        ...pinnedProjects,
        ...(Array.isArray(projectData) ? [...projectData] : [])
      ]

      // checking if last selected project is in the list or not
      const lastSelectedProject = window.localStorage.getItem(
        "last_selected_project"
      )
      if (
        lastSelectedProject &&
        projectNames
          ?.map((project) => project?.u_number)
          .indexOf(lastSelectedProject) > -1
      ) {
        dispatch(settingsActions.setProject(lastSelectedProject))
      } else {
        dispatch(
          settingsActions.setProject(
            projectNames ? projectNames[0]?.u_number : ""
          )
        )
      }

      dispatch(userActions.userProjectNames(projectNames))
      dispatch(userActions.userProjectsFetching(false))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ; (async () => {
      try {
        if (selectedProjectId) {
          window.localStorage.setItem(
            "last_selected_project",
            selectedProjectId
          )
          setProjectBondLoading(true)
          dispatch(userActions.userAccountsFetching(true))
          const portalComponentAccess = (
            await getPortalComponentAccess({ auth, host }, selectedProjectId)
          ).data
          const modularity = { ...modularityArray }
          portalComponentAccess.u_portal_components
            .split(",")
            .forEach((component) => {
              modularity[component.trim()] = true
            })
          dispatch(
            userActions.setAllowedPortalComponents({
              ...Object.entries(modularity).reduce((acc, [k, v]) => {
                acc[k] =
                  v === true
                    ? true
                    : clientView
                      ? NOT_ACCESSIBLE_TO_CLIENT
                      : false
                return acc
              }, {})
            })
          )
          const accList = (
            await getAccountList({ auth, host }, selectedProjectId)
          ).data

          const sortOrder = [
            "production",
            "non prod",
            "development",
            "stub",
            "disaster recovery",
            "sandbox",
            ""
          ]
          if (accList && accList.account_attribute)
            accList.account_attribute.sort((firstAccount, secondAccount) => {
              let firstAccEnvIndex = sortOrder.indexOf(
                firstAccount.account_environment.toLowerCase()
              )
              let secondAccEnvIndex = sortOrder.indexOf(
                secondAccount.account_environment.toLowerCase()
              )
              firstAccEnvIndex =
                firstAccEnvIndex < 0 ? sortOrder.length : firstAccEnvIndex
              secondAccEnvIndex =
                secondAccEnvIndex < 0 ? sortOrder.length : secondAccEnvIndex
              return firstAccEnvIndex - secondAccEnvIndex
            })

          if (accList) {
            dispatch(userActions.setAccounts(accList))
            setSelectedAccounts(accList.account_attribute)
            setAllAccounts(accList.account_attribute)
            setCloudabilityData(accList.cloudability_data)
          }

          dispatch(userActions.userAccountsFetching(false))
          const projBondingData = (
            await getProjectBonding({ auth, host }, selectedProjectId)
          ).data
          if (projBondingData) {
            setProjectBonding(projBondingData)
          }
          setProjectBondLoading(false)
          dispatch(userActions.userProjectsFetching(false))
        }
        setApiFailed(false)
      } catch {
        setApiFailed(true)
        setIsModalOpen(true)
      } finally {
        setProjectBondLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId])

  useEffect(() => {
    if (apiFailed) {
      setIsModalOpen(true)
    }
  }, [apiFailed, currentPageName])

  // Fetching counts of EMPTY account from all states
  const incidentParams = `account_id=EMPTY&state=${allIncidentStates}`
  const crParams = `account_id=EMPTY&state=${allChangeRequestStates}`

  const { data: incidentData } = useSWR(
    () =>
      selectedProjectId
        ? `/projects/${selectedProjectId}/get_incident_count?${incidentParams}`
        : null,
    () =>
      batchAPICalls(auth, extension, selectedProjectId, [incidentParams], getIncidentCount),
    { suspense: false, revalidateOnFocus: false }
  )

  const { data: crData } = useSWR(
    () =>
      selectedProjectId
        ? `/projects/${selectedProjectId}/get_Cr_count?${crParams}`
        : null,
    () => batchAPICalls(auth, extension, selectedProjectId, [crParams], getCrCount),
    { suspense: false, revalidateOnFocus: false }
  )

  useEffect(() => {
    if (incidentData && crData) {
      if (
        (pathname === "/" || pathname === "/service") &&
        isEmptyAccRequired(incidentData[0].data, crData[0].data)
      ) {
        // Add Empty Account if the current page is (overall analytics or ITSM) and if it is required and if it is not present
        const currentProjectName = userProjectNames.find(
          (project) => project.u_number === selectedProjectId
        )?.u_project_name
        if (!isEmptyAccPresent(selectedAccounts) && isEmptyAccSelected) {
          setSelectedAccounts(addEmptyAcc(selectedAccounts, currentProjectName))
        }
        if (!isEmptyAccPresent(accounts)) {
          setAllAccounts(addEmptyAcc(accounts, currentProjectName))
          // setSelectedAccounts(addEmptyAcc(selectedAccounts, currentProjectName))
        }
      } else {
        if (isEmptyAccPresent(selectedAccounts)) {
          setSelectedAccounts(removeEmptyAcc(selectedAccounts))
        }
        if (isEmptyAccPresent(accounts)) {
          setAllAccounts(removeEmptyAcc(accounts))
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentData, crData, pathname, accounts])

  return (
    <>
      {isModalOpen && (
        <ConfirmModal
          show={isModalOpen}
          setShow={setIsModalOpen}
          message={`The selected project "${currentProjectName}" does not have any pages to display. Please reach out to SDM/Ops Lead. You may choose other project to continue.`}
          onOkay={() => {
            setIsModalOpen(false)
          }}
        />
      )}
    </>
  )
}
